import React, { useEffect, useMemo, useCallback, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import ActionBar from "../../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../../components/Table/DIndentTable2";
import Header from "../../../../components/Header/Header";
import Footer from "../../../../components/Footer/Footer";
import { fetchClosedIndentData, fetchDropdownOptions, filterIndents } from '../../../../store/dindentSlice';

const LoadingSpinner = memo(() => (
  <div className="flex justify-center items-center p-4">
    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
  </div>
));

const MemoizedDynamicTable = memo(DynamicTable);
const MemoizedActionBar = memo(ActionBar);

const Indent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role, id: enteredBy } = useSelector((state) => state.auth);
  const {
    indents,
    filteredIndents,
    dropdownData,
    isLoading,
    error
  } = useSelector((state) => state.indent);

  const [selectedRow, setSelectedRow] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/dindent/dclosedindent`;

  useEffect(() => {
    dispatch(fetchClosedIndentData({ role, enteredBy }));
    dispatch(fetchDropdownOptions());
  }, [dispatch, role, enteredBy]);

  const getEditFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData.projects.map((project) => ({
        value: project.id,
        label: project.name,
      })),
      className: "w-full",
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData.types.map((type) => ({
        value: type.id,
        label: type.name,
      })),
      className: "w-full",
    },
  ], [dropdownData.projects, dropdownData.types]);

  const handleSearch = useCallback((query) => {
    console.log('Search query received:', query); // Debug log
    if (!query || query.trim() === '') {
      console.log('Empty query, resetting to all indents'); // Debug log
      dispatch(filterIndents(indents));
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    console.log('Searching for term:', searchTerm); // Debug log
    
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.ProjectName,
        indent.TypeName,
        indent.Status,
        indent.CreationDate,
        indent.TaskSchedule
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    });
    
    console.log('Filtered results count:', filtered.length); // Debug log
    dispatch(filterIndents(filtered));
  }, [dispatch, indents]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (!dateFrom || !dateTo) {
      dispatch(filterIndents(indents));
      return;
    }

    const filtered = indents.filter((indent) => {
      const creationDate = new Date(indent.CreationDate);
      return creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo);
    });
    dispatch(filterIndents(
      filtered.sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate))
    ));
  }, [indents, dispatch]);

  const handleRowClick = useCallback((row) => {
    navigate(`/viewTable`, { state: { row } });
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <p className="text-red-500 text-center">Error: {error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="flex-grow p-2 sm:p-4">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold">View Table</h3>
        </div>

        <div className="flex flex-col gap-4">
          {/* ActionBar - Always at the top 
          <div className="bg-white p-4 rounded-lg shadow-sm">
            {dropdownData?.users?.length > 0 && dropdownData?.projects?.length > 0 && (
              <MemoizedActionBar
                title="Indent"
                fields={getEditFields}
                apiEndpoint={apiEndpoint}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onRefresh={() => dispatch(fetchClosedIndentData({ role, enteredBy }))}
                onSearch={handleSearch}
                onFilter={handleDateFilter}
                rowKey="IndentID"
                userRole={role}
                addLink="/addIndent"
                editRoute="/viewTable"
                className="w-full"
              />
            )}
          </div>
          */}

          {/* Table - Below ActionBar */}
          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-4">
            <div className="overflow-x-auto">
              <MemoizedDynamicTable
                columns={[
                  { key: "IndentID", label: "ID" },
                  { key: "CreationDate", label: "Date" },
                  { key: "ProjectName", label: "Project" },
                  { key: "TypeName", label: "Type" },
                  { key: "Status", label: "Status" },
                  { key: "ItemCount", label: "Items" },
                ]}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onDoubleClick={handleRowClick}
                rowKey="IndentID"
                userIdKey="UserID"
                highlightColor="bg-blue-100"
                noDataMessage="No indents found."
                userRole={role}
                mobileView={true}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default memo(Indent);